import { ArticleContent } from '@components/article/ArticleContent/ArticleContent';
import { ArticleFooter } from '@components/article/ArticleFooter/ArticleFooter';
import { ArticleHeader } from '@components/article/ArticleHeader/ArticleHeader';
import { AdContainer } from '@components/common/AdContainer/AdContainer';
import { FormatType } from '@consts/wysiwyg';
import { useAssets } from '@hooks/useAssets';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import { useScrollPercentage } from '@hooks/useScrollPercentage';
import { increaseArticleViews } from '@queries/increaseArticleViews';
import { useNewRelatedArticles } from '@queries/useRelatedArticles';
import { MEDIA_QUERIES, useMediaQuery } from '@student-coin/coinpaper-components';
import {
  countRelatedArticleInContent,
  mergeRelatedArticleDataIntoContent,
} from '@utils/articleContent';
import dynamic from 'next/dist/shared/lib/dynamic';
import { ReactElement, useEffect, useMemo, useRef } from 'react';
import { useCookies } from 'react-cookie';
import tw from 'twin.macro';
import { StickyShareArticle } from '../ShareArticle/StickyShareArticle/StickyShareArticle';
import { Container, Divider, Wrapper } from './Article.styles';
import { ArticleProps } from './Article.types';

const Sidebar = dynamic(() => import('../Sidebar/Sidebar').then((mod) => mod.Sidebar), {
  ssr: false,
});

const AdPlacementComponent = dynamic(
  () => import('@components/common/AdPlacement').then((mod) => mod.AdPlacement),
  { ssr: false },
);

const isParagraphEmpty = (node) => {
  if (node?.children?.length > 1) {
    return false;
  }

  if (node?.children?.length === 1) {
    return !(node.children[0]?.text?.trim()?.length > 0); // we use paragraphs for empty lines break
  }

  return true;
};

const insertInContentAds = (content, id) => {
  const savedIndexes = [];
  let paragraphsCount = 0;

  const allParagraphs = content.filter(
    (el) => el.type === 'paragraph' && !isParagraphEmpty(el),
  ).length;

  const adIndexes = [0, 2];
  let counter = 3;
  let adElement = 6;

  while (adElement <= 50) {
    adIndexes.push(adElement);
    adElement += counter;
    counter += 1;
  }

  for (let i = 0; i < content.length; i += 1) {
    const el = content[i];

    const isParagraph = el.type === 'paragraph';
    const isNotEmpty = !isParagraphEmpty(el);
    const isInBounds = i < 3 || i + 4 <= allParagraphs;

    if (isParagraph && isNotEmpty && isInBounds) {
      // ads should be inserted after 1st and 3rd paragraph
      if (adIndexes.includes(paragraphsCount)) {
        savedIndexes.push(i);
      }
      paragraphsCount += 1;
    }
  }

  const contentCopy = [...content];

  for (let i = 0; i < savedIndexes.length; i += 1) {
    contentCopy.splice(savedIndexes[i] + i + 1, 0, {
      type: FormatType.ad,
      children: [],
      adPlacement: `classic-article-in-content-${adIndexes[i] + 1}-paragraph`,
      adId: `classic-article-in-content-${adIndexes[i] + 1}-paragraph-${id}`,
    });
  }

  return contentCopy;
};

export const Article = ({
  id,
  author,
  content,
  description,
  divider = false,
  publishedAt,
  publishedDate,
  sponsored,
  categories,
  subcategories,
  title,
  thumbnail,
  updatedAt,
  url,
  readTimeInMinutes,
  slug,
  articleAudioId,
  onEndReach,
  onArticleVisible,
  tags,
  ads,
  assets,
  affiliateLinks,
  affiliateLinksHeading,
  topReads,
  latestNews,
}: ArticleProps): ReactElement => {
  const articleParsedContent = useMemo(() => (content ? JSON.parse(content) : []), [content]);
  const relatedArticlesCountInContent = countRelatedArticleInContent(articleParsedContent);
  const { data: relatedArticles = [] } = useNewRelatedArticles(id);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isTabletWide = useMediaQuery(MEDIA_QUERIES.tablet);
  const endReachedOnce = useRef(false);
  const [cookies] = useCookies(['cookies']);

  const coinPrices = useAssets(assets);
  useEffect(() => {
    increaseArticleViews(id);
  }, [id]);

  const footerIntersectionEntry = useIntersectionObserver(footerRef, {
    threshold: 0,
  });
  const containerIntersectionEntry = useIntersectionObserver(containerRef, {
    threshold: 0,
  });

  const articleEndReached = !!footerIntersectionEntry?.isIntersecting;
  const isVisible = !!containerIntersectionEntry?.isIntersecting;

  const { scrollPercentage } = useScrollPercentage(containerRef, isVisible);

  const showStickyShareArticle = useMemo(() => {
    return (
      scrollPercentage > 20 &&
      scrollPercentage < 100 &&
      isVisible &&
      !articleEndReached &&
      cookies.cookies
    );
  }, [scrollPercentage, isVisible, articleEndReached, cookies.cookies]);

  const mergedArticleContent = useMemo(
    () =>
      insertInContentAds(
        mergeRelatedArticleDataIntoContent(
          articleParsedContent,
          relatedArticles,
          relatedArticlesCountInContent,
        ),
        id,
      ),
    [relatedArticles, articleParsedContent, relatedArticlesCountInContent, id],
  );

  useEffect(() => {
    if (isVisible && onArticleVisible) {
      onArticleVisible(id, slug);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isVisible]);

  useEffect(() => {
    if (articleEndReached && !endReachedOnce.current) {
      endReachedOnce.current = true;
      onEndReach(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, articleEndReached]);

  return (
    <Wrapper>
      <Container ref={containerRef}>
        {/* TODO: use main subcategory */}
        <ArticleHeader
          id={id}
          title={title}
          slug={slug}
          description={description}
          updatedAt={updatedAt}
          publishedDate={publishedDate}
          publishedAt={publishedAt}
          author={author}
          sponsored={sponsored}
          category={categories[0]}
          subcategory={subcategories?.[0] ?? null}
          readTimeInMinutes={readTimeInMinutes}
          articleAudioId={articleAudioId}
          ads={ads}
          assets={coinPrices}
        />
        <ArticleContent value={mergedArticleContent} thumbnail={thumbnail} ads={ads}>
          <StickyShareArticle
            link={url}
            visible={showStickyShareArticle || isTabletWide}
            textToShare={title}
          />
        </ArticleContent>
        <div css={tw`w-full col-span-12 place-self-center`}>
          <AdContainer adPlacements={ads} placement="classic-article-footer-banner">
            <AdPlacementComponent adPlacements={ads} placement="classic-article-footer-banner" />
          </AdContainer>
        </div>
        <ArticleFooter
          url={url}
          author={author}
          relatedArticles={relatedArticles}
          ref={footerRef}
          textToShare={title}
          tags={tags}
          affiliateLinks={affiliateLinks}
          topReads={topReads}
          latestNews={latestNews}
        />
        {divider && <Divider />}
      </Container>
      <Sidebar
        ads={ads}
        assets={coinPrices}
        affiliateLinks={affiliateLinks}
        affiliateLinksHeading={affiliateLinksHeading}
        topReads={topReads}
        latestNews={latestNews}
      />
    </Wrapper>
  );
};
